import React, { useContext, useEffect, useRef, useState } from "react";
import api from "../api";
import { AuthContext } from "../context/AuthContext";
import RequestHistoryModal from "../components/RequestHistoryModal";
import OfferCard from "../components/OfferCard";
import DeclineModal from "../components/DeclineModal";
import "./RequestsPage.css";
import { User } from "../types/User";
import { FaGlobe, FaQrcode } from "react-icons/fa";
import { FaTriangleExclamation as FaExclamationTriangle } from "react-icons/fa6";
import { Link, useNavigate } from "react-router-dom";
import { roundPriceStep } from "../utils/roundPriceStep";
import { SERVER_URL } from "../config";
import PhonePromptModal from "../components/PhonePromptModal";

interface Order {
  _id: string;
  searchQuery: string;
  amount?: number;
  comment?: string;
  paid: boolean;
  orderNumber?: string;
  createdAt: string;
  status: "pending" | "accepted" | "declined";
  trackId: string;
  trackImage?: string;
  trackArtists: string[];
  isPriority?: boolean;
  tier?: "standard" | "priority" | "fast" | null;
  currency?: string;
  clientNickname?: string | null;
  clientInstagram?: string | null;
}

type EarningsTotals = Record<string, { total?: number; count?: number }>;

const num = (v: any) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const normCur = (cur?: string) => {
  const c = String(cur || "")
    .trim()
    .toLowerCase();
  return c && c !== "null" && c !== "undefined" ? c : "usd";
};
const curSym = (cur?: string) => {
  switch (normCur(cur)) {
    case "usd":
      return "$";
    case "eur":
      return "€";
    case "gbp":
      return "£";
    case "rub":
      return "₽";
    default:
      return "";
  }
};
const fmtMoney = (cur: string | undefined, n: any) =>
  `${curSym(cur)}${num(n).toFixed(2)}`;

// Универсальный нормализатор абсолютных ссылок
const absUrl = (u?: string) =>
  !u ? "" : (u.startsWith("http") ? u : `${SERVER_URL}${u}`);

const RequestsPage: React.FC = () => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const [pendingOffers, setPendingOffers] = useState<Order[]>([]);
  const [acceptedOffers, setAcceptedOffers] = useState<Order[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showCashoutModal, setShowCashoutModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showFansModal, setShowFansModal] = useState(false);
  const [fans, setFans] = useState<{ nickname?: string | null; instagram?: string | null; requestCount: number }[]>([]);
  const [price, setPrice] = useState<number>(user?.pricePerRequest ?? 1);
  const [profile, setProfile] = useState<User | null>(null);
  const [editingPrice, setEditingPrice] = useState<boolean>(false);
  const priceInputRef = useRef<HTMLInputElement | null>(null);
  const [earningsByCur, setEarningsByCur] = useState<EarningsTotals>({});

  const prevEarningsRef = useRef<EarningsTotals>({});

  const navigate = useNavigate();
  const [showPhonePrompt, setShowPhonePrompt] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!isAuthenticated || !user) return;
      try {
        const res = await api.get<User>(`/users/${user.slug}`);
        setProfile(res.data);
        setPrice(res.data.pricePerRequest ?? 10);
        if (!res.data.phoneNumber) {
          setShowPhonePrompt(true);
        }
      } catch (err) {
        console.error("Ошибка при загрузке профиля:", err);
      }
    };
    fetchProfile();
  }, [user, isAuthenticated]);

  const fetchAll = async () => {
    try {
      const [pendingRes, acceptedRes, earningsRes] = await Promise.all([
        api.get<Order[]>("/orders/pending"),
        api.get<Order[]>("/orders/accepted"),
        api.get<any>("/orders/earnings"),
      ]);

      setPendingOffers(pendingRes.data || []);
      setAcceptedOffers(acceptedRes.data || []);

      // Нормализация earnings: поддерживаем и { byCurrency }, и старый { total }
      const payload = earningsRes?.data || {};
      let byCur: EarningsTotals = {};

      if (
        payload.byCurrency &&
        typeof payload.byCurrency === "object" &&
        !Array.isArray(payload.byCurrency)
      ) {
        for (const [k, v] of Object.entries<any>(payload.byCurrency)) {
          const cur = normCur(k);
          const prev = byCur[cur] || { total: 0, count: 0 };
          byCur[cur] = {
            total: prev.total! + num(v?.total),
            count: prev.count! + num(v?.count),
          };
        }
      } else if (typeof payload.total === "number") {
        const fallbackCur = normCur(profile?.currency || "usd");
        byCur[fallbackCur] = { total: num(payload.total), count: 0 };
      }

      setEarningsByCur(byCur);
    } catch (err) {
      console.error("Fetch error:", err);
      setPendingOffers([]);
      setAcceptedOffers([]);
      setEarningsByCur({});
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchAll();
    const interval = setInterval(fetchAll, 10000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const handleOfferAction = async (
    id: string,
    action: "accept" | "decline"
  ) => {
    try {
      const url = `/orders/${id}/${action}`;
      await api.post(url);
      await fetchAll();
    } catch (err: any) {
      const serverMsg =
        err?.response?.data?.msg || err?.message || "Unknown error";
      console.error(`Не удалось ${action} заявку:`, serverMsg);
    }
  };

  const [declineOrderId, setDeclineOrderId] = useState<string | null>(null);
  const closeDeclineModal = () => setDeclineOrderId(null);
  const confirmDecline = async (
    reason: "pas_ambience" | "no_access" | "more_money",
    counterAmount?: number
  ) => {
    if (!declineOrderId) return;
    try {
      await api.post(`/orders/${declineOrderId}/decline`, {
        reason,
        counterAmount,
      });
      closeDeclineModal();
      await fetchAll();
    } catch (err: any) {
      console.error("Decline error:", err?.response?.data || err?.message);
    }
  };

  const handlePlay = async (id: string) => {
    await api.post(`/orders/${id}/play`);
    await fetchAll();
  };

  // Приводим любые относительные пути к абсолютным
  const getAvatarUrl = (url?: string) => absUrl(url);

  const handlePriceUpdate = async () => {
    if (!profile) return;
    try {
      const fixed = roundPriceStep(price);
      await api.patch(`/users/${profile.slug}/price`, { price: fixed });
      const refreshed = await api.get<User>(`/users/${profile.slug}`);
      setProfile(refreshed.data);
      setPrice(roundPriceStep(refreshed.data.pricePerRequest ?? 0));
      setEditingPrice(false);
    } catch (err) {
      console.error("Ошибка обновления цены:", err);
    }
  };

  const saveIfChanged = async () => {
    if (!profile) return;
    const fixed = roundPriceStep(num(price));
    if (fixed !== roundPriceStep(num(profile.pricePerRequest))) {
      setPrice(fixed);
      await handlePriceUpdate();
    } else {
      setEditingPrice(false);
    }
  };

  const openCashoutModal = () => setShowCashoutModal(true);
  const closeCashoutModal = () => setShowCashoutModal(false);

  const handleConfirmCashout = async () => {
    setShowCashoutModal(false);

    const prev = earningsByCur;
    prevEarningsRef.current = prev;

    try {
      await api.post("/payments/cashout");

      setEarningsByCur({});

      setSuccessMessage(
        "Cashout completed. Check your email for a confirmation."
      );
      setTimeout(() => setSuccessMessage(""), 3500);

      setTimeout(() => {
        fetchAll();
      }, 1500);
    } catch (err: any) {
      console.error("Cashout error:", err?.response?.data || err);

      setEarningsByCur(prevEarningsRef.current || {});
      setSuccessMessage("Cashout failed. Please try again.");
      setTimeout(() => setSuccessMessage(""), 3500);
    }
  };

  const onPriceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handlePriceUpdate();
    } else if (e.key === "Escape") {
      const p = profile?.pricePerRequest;
      setPrice(typeof p === "number" ? p : 0);
      setEditingPrice(false);
    }
  };

  const onPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(",", ".").replace(/[^0-9.]/g, "");
    const parts = v.split(".");
    const sanitized =
      parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : v;
    setPrice(sanitized !== "" ? Number(sanitized) : 0);
  };

  const hasFunds = Object.values(earningsByCur).some((v) => num(v?.total) > 0);

  const visualTier = (t: Order["tier"]) => (t === "fast" ? "fast" : "standard");

  return (
    <div className="requests-container">
      {showPhonePrompt && (
        <PhonePromptModal
          onSaved={() => setShowPhonePrompt(false)}
          onDismiss={() => setShowPhonePrompt(false)}
        />
      )}
      <div
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "4px 12px", borderRadius: 20,
          background: profile?.isAcceptingRequests !== false ? "rgba(34,197,94,0.12)" : "rgba(107,114,128,0.12)",
          marginBottom: 12,
        }}
      >
        <div style={{
          width: 7, height: 7, borderRadius: "50%",
          background: profile?.isAcceptingRequests !== false ? "#22c55e" : "#6b7280",
          boxShadow: profile?.isAcceptingRequests !== false ? "0 0 6px rgba(34,197,94,0.5)" : "none",
        }} />
        <span style={{
          fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.03em",
          color: profile?.isAcceptingRequests !== false ? "#22c55e" : "#9ca3af",
        }}>
          {profile?.isAcceptingRequests !== false ? "Live" : "Offline"}
        </span>
      </div>

      <div className="req-av-block">
        <div className="req-avatar-wrap">
          {profile ? (
            profile.avatarUrl ? (
              <img
                src={getAvatarUrl(profile.avatarUrl)}
                alt="Avatar"
                className="request-avatar-image"
                onError={(e) => (e.currentTarget.src = "/default_avatar.jpg")}
              />
            ) : (
              <div className="avatar-placeholder">
                {(profile?.name?.charAt(0) ?? "?").toUpperCase()}
              </div>
            )
          ) : (
            <div className="avatar-placeholder">?</div>
          )}
          <Link
            to={`/qr/${profile?.slug || ""}`}
            className="qr-badge"
            title="View QR Code"
          >
            <FaQrcode size={11} />
          </Link>
        </div>

        <h1 className="user-namen-rq">{profile?.name || ""}</h1>

        <div className="btn-req-head-a">
          <button
            className="connected-btn"
            onClick={async () => {
              try {
                const res = await api.get("/orders/fans");
                setFans(res.data || []);
              } catch { setFans([]); }
              setShowFansModal(true);
            }}
          >
            <FaGlobe /> Fans
          </button>

          <div className="price-edit-wrapper">
            {!editingPrice ? (
              <div
                className="price-display"
                onClick={() => setEditingPrice(true)}
                style={{ cursor: "pointer", display: "inline-block" }}
              >
                {price === 0
                  ? "Request: Free"
                  : `Request: ${curSym(profile?.currency || "usd")}${roundPriceStep(num(price)).toFixed(2)}`}
              </div>
            ) : (
              <div
                className="price-input-container"
                style={{ position: "relative", display: "inline-block" }}
              >
                <span
                  className="dollar-sign"
                  style={{
                    position: "absolute",
                    left: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    color: "#555",
                  }}
                >
                  {curSym(profile?.currency || "usd")}
                </span>

                <input
                  ref={priceInputRef}
                  autoFocus
                  type="text"
                  inputMode="decimal"
                  placeholder="0"
                  className="price-input-inline"
                  value={String(price)}
                  onChange={onPriceChange}
                  onKeyDown={onPriceKeyDown}
                  onBlur={saveIfChanged}
                  style={{ paddingLeft: "1.5em" }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="queued">
        <h2>Queued</h2>
        {acceptedOffers.map((o) => (
          <OfferCard
            key={o._id}
            _id={o._id}
            searchQuery={o.searchQuery}
            trackId={o.trackId}
            amount={num(o.amount)}
            avatarUrl={getAvatarUrl(o.trackImage || "")}
            trackArtists={Array.isArray(o.trackArtists) ? o.trackArtists : []}
            status="accepted"
            comment={o.comment}
            onPlay={() => handlePlay(o._id)}
            onDecline={() => setDeclineOrderId(o._id)}
            isPriority={o.tier === "fast"}
            tier={visualTier(o.tier ?? null)}
            currency={o.currency || profile?.currency}
            createdAt={o.createdAt}
            clientNickname={o.clientNickname}
            clientInstagram={o.clientInstagram}
          />
        ))}
      </div>

      <div className="pending">
        <div className="pending-anim-block">
          <h2>Pending</h2>
        </div>

        {pendingOffers.length > 0 && (
          <>
            <div className="pending-bars">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
            <div className="swipe-hint">Swipe for Actions</div>
          </>
        )}
        {pendingOffers.map((o) => (
          <OfferCard
            key={o._id}
            _id={o._id}
            searchQuery={o.searchQuery}
            amount={num(o.amount)}
            trackId={o.trackId}
            avatarUrl={getAvatarUrl(o.trackImage || "")}
            trackArtists={Array.isArray(o.trackArtists) ? o.trackArtists : []}
            status="pending"
            comment={o.comment}
            onAccept={() => handleOfferAction(o._id, "accept")}
            onDecline={() => setDeclineOrderId(o._id)}
            isPriority={o.tier === "fast"}
            tier={visualTier(o.tier ?? null)}
            currency={o.currency || profile?.currency}
            createdAt={o.createdAt}
            clientNickname={o.clientNickname}
            clientInstagram={o.clientInstagram}
          />
        ))}
        <DeclineModal
          isOpen={!!declineOrderId}
          onClose={closeDeclineModal}
          onConfirm={confirmDecline}
        />
      </div>

      {successMessage && (
        <div className="cashout-success-message">{successMessage}</div>
      )}

      <div className="earnings-bar">
        <span className="earnings">
          {Object.keys(earningsByCur).length === 0
            ? "Earnings: 0"
            : Object.entries(earningsByCur)
                .map(([cur, v]) => fmtMoney(cur, v?.total))
                .join("  •  ")}
        </span>

        <button className="history-btn" onClick={() => setShowModal(true)}>
          Request History
        </button>

        <button
          className="cashout-btn"
          onClick={openCashoutModal}
          title={hasFunds ? "Cashout" : "No funds to cash out"}
          disabled={!hasFunds}
        >
          Cashout
        </button>
      </div>

      {showModal && <RequestHistoryModal onClose={() => setShowModal(false)} />}

      {showCashoutModal && (
        <div className="cashout-modal-overlay">
          <div className="cashout-modal">
            <div className="cashout-modal-header">
              <FaExclamationTriangle size={24} className="cashout-modal-icon" />
              <h2 className="cashout-modal-title">Cashout Confirmation</h2>
            </div>
            <div className="cashout-modal-body">
              {!hasFunds ? (
                <p
                  style={{
                    textAlign: "center",
                    fontSize: "16px",
                    color: "#cdd1d8",
                  }}
                >
                  You don't have any funds to withdraw yet.
                </p>
              ) : (
                <>
                  {Object.entries(earningsByCur).map(([cur, v]) => {
                    const total = num(v?.total);
                    const fee = total * 0.15;
                    const net = total - fee;
                    return (
                      <div key={cur} style={{ marginBottom: 8 }}>
                        <p className="cashout-modal-info">
                          <span>Total:</span>{" "}
                          <strong>{fmtMoney(cur, total)}</strong>
                        </p>
                        <p className="cashout-modal-info">
                          <span>Request Fee (15%):</span>{" "}
                          <strong>{fmtMoney(cur, fee)}</strong>
                        </p>
                        <p className="cashout-modal-info">
                          <span>Final DJ Amount:</span>{" "}
                          <strong>{fmtMoney(cur, net)}</strong>
                        </p>
                      </div>
                    );
                  })}
                  <p className="cashout-modal-note">
                    Be sure to accept all song requests you played before
                    cashing out
                  </p>
                </>
              )}
            </div>

            <div className="cashout-modal-actions">
              <button
                className="cashout-cancel-btn"
                onClick={closeCashoutModal}
              >
                Cancel
              </button>
              {hasFunds && (
                <button
                  className="cashout-confirm-btn"
                  onClick={handleConfirmCashout}
                >
                  Confirm
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showFansModal && (
        <div
          className="soon-modal-overlay"
          onClick={() => setShowFansModal(false)}
        >
          <div className="soon-modal fans-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Fans</h3>
            {fans.length === 0 ? (
              <p style={{ color: "#9ca3af" }}>No fans yet. They’ll show up here when clients send requests.</p>
            ) : (
              <div className="fans-list">
                {fans.map((f, i) => (
                  <div key={i} className="fan-row">
                    <div className="fan-info">
                      {f.nickname && <span className="fan-nick">{f.nickname}</span>}
                      {f.instagram && (
                        <a
                          href={`https://instagram.com/${f.instagram}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="fan-ig"
                        >
                          @{f.instagram}
                        </a>
                      )}
                    </div>
                    <span className="fan-count">{f.requestCount} req{f.requestCount !== 1 ? "s" : ""}</span>
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => setShowFansModal(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestsPage;
